import { Fragment, useEffect, useRef, useState } from 'react'
import { useAttachmentStore, useIsMaxAttachmentsReached } from '../../../store'
import { useActions } from '../../../providers'
import { ChatAttachment, ScreenshotDisplay, ScreenshotSources, ScreenshotWindow } from '../../../types'
import { AttachmentMenu, AttachmentMenuHandle, AttachmentMenuItem } from './attachment-menu'
import { useFocusChatInput } from '../input/hooks/use-focus-chat-input'
import { useAtom } from 'jotai'
import { FadingScrollable } from '../../elements/FadingScrollable'
import { cn, logAmplitudeEvent } from '../../../utils'
import { contextSourcesAtom } from '../../../store/atoms'
import { useTrackAddAttachment } from '../../../hooks/attachments-hooks'
import { ImageIcon } from 'lucide-react'

export const ScreenshotPicker = ({
  buttonClassName,
  onAttach,
}: {
  buttonClassName?: string
  onAttach?: (attachment?: ChatAttachment) => void
}) => {
  const menuRef = useRef<AttachmentMenuHandle>(null)
  const [isOpen, setIsOpen] = useState(false)
  const isMaxAttachmentsReached = useIsMaxAttachmentsReached()

  const onOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      logAmplitudeEvent('Open Attachment Picker', { type: 'image' })
    }
  }

  return (
    <AttachmentMenu
      ref={menuRef}
      icon={<ImageIcon size={20} />}
      tooltip={'Use Screenshot'}
      buttonClassName={cn(
        'hover:text-pink-subtle',
        isOpen ? 'text-pink-subtle bg-pink/15 hover:bg-pink/20' : '',
        buttonClassName,
        isMaxAttachmentsReached ? 'opacity-35 pointer-events-none' : '',
      )}
      menuClassName={'max-w-64 min-w-64 p-0'}
      onOpenChange={onOpenChange}
    >
      <WindowSelectionMenu onAttach={onAttach} />
    </AttachmentMenu>
  )
}

export const WindowSelectionMenu = ({
  includeDisplays,
  isSearchable,
  isScrollable,
  onAttach,
  className,
}: {
  className?: string
  isSearchable?: boolean
  isScrollable?: boolean
  includeDisplays?: boolean
  onAttach?: (attachment?: ChatAttachment) => void
}) => {
  const { addScreenshotAttachment } = useAttachmentStore()
  const { getScreenshotSources, getWindowScreenshot } = useActions()
  const [sources, setSources] = useAtom(contextSourcesAtom)
  const [filteredSources, setFilteredSources] = useState<ScreenshotSources>({ windows: [], displays: [] })
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const itemsRef = useRef<(HTMLDivElement | null)[]>([])
  const menuRef = useRef<AttachmentMenuHandle>(null)
  const focusInput = useFocusChatInput()
  const isMaxAttachmentsReached = useIsMaxAttachmentsReached()
  const trackAddAttachment = useTrackAddAttachment()

  const onSelectWindow = async (window: ScreenshotWindow) => {
    const windowImageUrl = await getWindowScreenshot(window.windowTitle)
    if (!windowImageUrl) {
      // @TODO handle error capturing screenshot
      console.error('Error capturing screenshot')
      return
    }
    addScreenshotAttachment(windowImageUrl, window.windowTitle)
    menuRef.current?.close()
    focusInput()
    onAttach?.()
    trackAddAttachment({ type: 'image (window)', trigger: 'Plus Button' })
  }

  const onSelectDisplay = async (display: ScreenshotDisplay) => {
    if (!display.thumbnail) {
      return
    }
    addScreenshotAttachment(display.thumbnail)
    menuRef.current?.close()
    focusInput()
    onAttach?.()
    trackAddAttachment({ type: 'image (display)', trigger: 'Plus Button' })
  }

  const fetchSources = async () => {
    const sources = await getScreenshotSources()
    setSources(sources)
    setFilteredSources(sources)
  }

  useEffect(() => {
    if (search?.length) {
      const searchLowercase = search.toLowerCase()
      setFilteredSources({
        windows: sources.windows.filter((window) => {
          const appNameLowercase = window.appName?.toLowerCase() ?? ''
          const windowTitleLowercase = window.windowTitle.toLowerCase()
          return (
            searchLowercase.includes(appNameLowercase) ||
            appNameLowercase.includes(searchLowercase) ||
            searchLowercase.includes(windowTitleLowercase) ||
            windowTitleLowercase.includes(searchLowercase)
          )
        }),
        displays: 'display'.includes(searchLowercase) ? sources.displays : [],
      })
    } else {
      setFilteredSources(sources)
    }
    // Reset selected index when search changes
    setSelectedIndex(-1)
  }, [sources, search])

  const allItems = [...filteredSources.windows, ...filteredSources.displays]

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const itemCount = allItems.length

    if (itemCount === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < itemCount - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < itemCount) {
          const item = allItems[selectedIndex]
          if (selectedIndex < filteredSources.windows.length) {
            onSelectWindow(item as ScreenshotWindow)
          } else {
            onSelectDisplay(item as ScreenshotDisplay)
          }
        }
        break
    }
  }

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < itemsRef.current.length) {
      itemsRef.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    fetchSources()
  }, [])

  const Container = isScrollable !== false ? FadingScrollable : Fragment

  return (
    <>
      {isSearchable !== false && (
        <div className={'p-2'}>
          <input
            ref={searchInputRef}
            autoFocus={true}
            className={
              'border-light-5 bg-dark-10 focus:border-light-20 text-light/80 placeholder:text-light/30 w-full rounded-xl border border-solid p-3 py-2.5 text-sm placeholder:text-sm'
            }
            placeholder={`Search ${sources.windows.length} windows${includeDisplays !== false ? ` and ${sources.displays.length} displays` : ''}`}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
      <Container
        className={cn('flex max-h-[270px] flex-col gap-1 overflow-auto overflow-x-hidden', className)}
        fadeSize="10%"
      >
        {filteredSources.windows.map((window, index) => {
          return (
            <AttachmentMenuItem
              key={`screenshot-${window.windowTitle}-${index}`}
              ref={(el) => (itemsRef.current[index] = el)}
              className={cn(
                selectedIndex === index ? 'text-light' : '',
                isMaxAttachmentsReached ? 'pointer-events-none opacity-35' : '',
              )}
              onClick={() => onSelectWindow(window)}
            >
              {window.appIcon && <img src={window.appIcon} className={'h-5 w-5'} />}
              <span className={'min-w-0 max-w-full truncate'}>{window.windowTitle}</span>
            </AttachmentMenuItem>
          )
        })}

        {includeDisplays !== false &&
          filteredSources.displays.map((display, displayIndex) => {
            const index = filteredSources.windows.length + displayIndex
            return (
              <AttachmentMenuItem
                key={`screenshot-display-${displayIndex}`}
                ref={(el) => (itemsRef.current[index] = el)}
                className={cn(
                  selectedIndex === index ? 'text-light' : '',
                  isMaxAttachmentsReached ? 'pointer-events-none opacity-35' : '',
                )}
                onClick={() => onSelectDisplay(display)}
              >
                <img src={display.thumbnail} className={'aspect-video h-5 rounded-md'} />
                <span className={'min-w-0 max-w-full truncate'}>Display {displayIndex + 1}</span>
              </AttachmentMenuItem>
            )
          })}
      </Container>
    </>
  )
}
